import React, { useContext, useState } from "react";

import InfoIcon from "@mui/icons-material/Info";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import mainStyles from "styles";

import { ContractStatus } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/contracts_pb";

import SubscriptionAction from "./subscription-action";
import {
  getOrgContractList,
  updateContractTerm,
} from "api/endpoints/contracts";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import DatePickerButton from "components/shared/date-picker-button";
import { DEFAULT_SNACKBAR_DURATION } from "constants/index";
import { ContractsListContext } from "context/organisation-context";
import { OrgDataContext } from "context/organisation-context";
import { SnackbarContext } from "context/snackbar-context";
import { SubscriptionActionsContext } from "context/subscriptions-action-context";
import {
  contractTermsInMonths,
  getMinContractEndDate,
  retrieveNextBillingDate,
} from "helpers/contracts";
import {
  convertTimestampToDate,
  convertToTimestamp,
  formatDateTimeDisplay,
} from "helpers/time";
import { UrlParamTypes } from "types/org-display";

const ChangeContractTerm = ({
  refreshOrgData,
  isZuora = false,
}: {
  refreshOrgData: () => void;
  isZuora?: boolean;
}) => {
  const classes = mainStyles();
  const { showError } = useContext(SnackbarContext);
  const { roleId, regionCode } = useParams<UrlParamTypes>();
  const { contractsList, setContractsList, getCurrentContract } =
    useContext(ContractsListContext);
  const { orgData } = useContext(OrgDataContext);

  // Default existing values from current contract/plan/subscription
  const currentContract = getCurrentContract(contractsList);
  const currentContractTermsInMonth = currentContract?.contractTermInMonths;
  const contractEndTimestamp = currentContract?.endDate;
  const currentContractEndDate =
    (contractEndTimestamp && convertTimestampToDate(contractEndTimestamp)) ||
    new Date();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [updatedContractTerm, setUpdatedContractTerm] = useState(
    currentContractTermsInMonth || 0
  );

  const [updatedContractEndDate, setUpdatedContractEndDate] = useState(
    retrieveNextBillingDate({
      date: new Date(),
      interval: updatedContractTerm,
    }) || new Date()
  );
  const { isSubscriptionActionLoading, setIsSubscriptionActionLoading } =
    useContext(SubscriptionActionsContext);

  const isSubmitBtnDisabled =
    isSubscriptionActionLoading ||
    !currentContract ||
    updatedContractTerm === 0;

  const isChangeContractBtnDisabled =
    !currentContract ||
    ![ContractStatus.TRIALING, ContractStatus.ACTIVATED].includes(
      currentContract.status
    ) ||
    (isZuora && currentContractTermsInMonth === 1);

  const handleUpdateContractEndDate = (newContractEndDate: Date) => {
    setUpdatedContractEndDate(newContractEndDate);
  };

  const handleContractTermChange = (selectedTerm: string) => {
    setUpdatedContractTerm(parseInt(selectedTerm));
    setUpdatedContractEndDate(
      retrieveNextBillingDate({
        date: new Date(),
        interval: parseInt(selectedTerm),
      }) || new Date()
    );
  };

  const handleIsModalOpen = () => {
    setIsModalOpen(true);
    setUpdatedContractEndDate(
      retrieveNextBillingDate({
        date: new Date(),
        interval: updatedContractTerm,
      }) || new Date()
    );
  };

  const handleOnConfirm = () => {
    setIsSubmitting(true);
    setIsSubscriptionActionLoading(true);
    const subscriptionId =
      orgData && orgData.subscription && orgData.subscription[0].id;

    updateContractTerm(
      {
        orgId: roleId,
        subscriptionId,
        contractTermInMonths: updatedContractTerm,
        contractEndDate: convertToTimestamp(updatedContractEndDate),
      },
      callOptionsHeaders(regionCode, roleId)
    )
      .then(() => {
        getOrgContractList(
          {
            orgId: roleId,
          },
          callOptionsHeaders(regionCode, roleId)
        )
          .then((res) => {
            setContractsList(res);
          })
          .catch(showError);

        refreshOrgData();
        setOpenSnackbar(true);
      })
      .catch(showError)
      .finally(() => {
        setIsSubmitting(false);
        setIsSubscriptionActionLoading(false);
      });
  };

  const resetState = () => {
    setUpdatedContractEndDate(
      retrieveNextBillingDate({
        date: new Date(),
        interval: updatedContractTerm,
      }) || new Date()
    );
    setUpdatedContractTerm(currentContractTermsInMonth || 0);
  };

  const displayContractTermOptions = () => {
    // For Zuora, if current term is > 1 month, exclude the 1-month option
    const availableTerms =
      isZuora && currentContractTermsInMonth && currentContractTermsInMonth > 1
        ? contractTermsInMonths.filter((term) => term !== 1)
        : contractTermsInMonths;

    return availableTerms.map((term) => {
      return (
        <MenuItem
          key={`contract-term-${term}`}
          value={term.toString()}
          data-anchor={"contract-term-option"}
          data-testid={"contract-term-option"}
        >
          {term}
        </MenuItem>
      );
    });
  };

  return (
    <>
      <SubscriptionAction
        title="Change Contract Term:"
        middleElem={
          <>
            {isSubmitting ? (
              <Grid
                container
                justifyContent="center"
                sx={{
                  ...classes.spaceTopSmall,
                  ...classes.spaceRightMedium,
                }}
              >
                <CircularProgress
                  color="primary"
                  variant="indeterminate"
                  thickness={4}
                  size={30}
                />
              </Grid>
            ) : (
              <Typography>
                <b>Term(s):</b> {`${currentContractTermsInMonth} month(s)`}
                <Tooltip title="Data from Contract Document" placement="right">
                  <IconButton
                    size="small"
                    sx={{ padding: "2px", marginLeft: "4px" }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <br />
                <b> End date: </b>{" "}
                {currentContractEndDate
                  ? `${formatDateTimeDisplay(currentContractEndDate)}`
                  : "N/A"}
                <Tooltip title="Data from Contract Document" placement="right">
                  <IconButton
                    size="small"
                    sx={{ padding: "2px", marginLeft: "4px" }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Typography>
            )}
          </>
        }
      >
        {isSubmitting ? (
          <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
            />
          </Grid>
        ) : (
          <Button
            data-testid="change-contract-term-btn"
            sx={{ mt: 1, mr: 2 }}
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => handleIsModalOpen()}
            disabled={isSubmitting || isChangeContractBtnDisabled}
          >
            Change Contract Term
          </Button>
        )}
      </SubscriptionAction>
      <ConfirmDialog
        title="Change Contract Term"
        open={isModalOpen}
        setOpen={setIsModalOpen}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Update"
        onConfirm={handleOnConfirm}
        onCancel={resetState}
        disabled={isSubmitting || isSubmitBtnDisabled}
        disableCancelBtn={isSubmitting}
      >
        <Grid container spacing={1}>
          <Grid item xs={12} paddingTop={1}>
            <Box
              display="flex"
              justifyContent="space-between"
              sx={classes.spaceTop}
            >
              <Typography className="space-top-x-small">
                <b>Contract Terms:</b>
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <FormControl
                  variant="outlined"
                  size="small"
                  sx={classes.formControl}
                  fullWidth
                >
                  <Select
                    id="select-contract-term"
                    defaultValue={currentContractTermsInMonth?.toString() || ""}
                    onChange={(event) =>
                      handleContractTermChange(event.target.value)
                    }
                    data-anchor="select-contract-term"
                    data-testid="select-contract-term"
                    inputProps={{
                      "data-testid": "select-contract-term-value",
                    }}
                  >
                    {displayContractTermOptions()};
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Grid>

          {currentContract?.status === ContractStatus.ACTIVATED && (
            <Grid item xs={12}>
              <Box
                display="flex"
                justifyContent="space-between"
                sx={classes.spaceTop}
              >
                <Typography className="space-top-x-small">
                  <b>Contract End Date:</b>
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <DatePickerButton
                    label="Pick new end date"
                    defaultDate={updatedContractEndDate}
                    onChange={(date) => {
                      handleUpdateContractEndDate(date);
                    }}
                    iconVariant
                    iconButtonSize="small"
                    disabled={
                      isSubmitting ||
                      currentContract?.status !== ContractStatus.ACTIVATED
                    }
                    minDate={getMinContractEndDate()}
                    maxDate={updatedContractEndDate}
                  />
                  <Typography>
                    {formatDateTimeDisplay(updatedContractEndDate)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>

        <Snackbar
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          open={openSnackbar}
          autoHideDuration={DEFAULT_SNACKBAR_DURATION}
          onClose={() => setOpenSnackbar(false)}
          data-testid="snackbar-change-contract-term"
        >
          <Alert severity="success" variant="filled">
            Contract updated
          </Alert>
        </Snackbar>
      </ConfirmDialog>
    </>
  );
};

export default ChangeContractTerm;
